/**
 * Шит выдачи премии/штрафа (05.09, владелец: «ОО в таб HR нужен новый субтаб
 * премии/штрафы»). Переключатель «Премия / Штраф» (предвыбран по нажатой
 * кнопке), сотрудник — Dropdown по operators HR, сумма — NumPadPop (A27: числа
 * только кастомным нумпадом), «За что» — TextPad (обязательно, ≤500), дата —
 * сегодня (todayBusiness) со стрелками ± день, для штрафа — необязательное
 * фото (FileReader → base64, ≤4 МБ, превью, крестик). «Выдать и отправить»:
 * премия → POST /api/bonuses { send: true }; штраф → POST /api/penalties, затем
 * POST /api/penalties/:id/send. Пока идёт отправка, шит не закрывается
 * («Отмена» disabled, backdrop/Esc игнорируются); ответ применяется только к
 * тому же открытию и живому компоненту (genRef/aliveRef — образец
 * op/PurchaseSheet.tsx). Сброс формы — при ОТКРЫТИИ, чтобы поля не мигали
 * пустыми во время анимации выезда.
 * 26.10 (владелец: «начислить тихо, без уведомлений… и особо отмечать в расчётке»):
 * «Без уведомления» — silent (строка молчит всю жизнь: отправка, правка, отмена),
 * «★ Отметить в расчётке» — slip_mark (подпись необязательна, ≤40).
 */
import { useEffect, useRef, useState, type ChangeEvent } from "react";
import { api } from "../../api/client";
import { ApiException } from "../../api/types";
import { Dropdown, NumPadPop, SheetDialog, Switch, TextPad } from "../../components/shared";
import { SlipMarkTag } from "../../components/shared/SlipMark";
import { SLIP_MARK_MAX } from "@ub/shared/slipMark";
import type { BoardOperator } from "../../hooks/useOoDay";
import { addDays, fmtDate, fmtDateApi, todayBusiness } from "../../utils/date";
import css from "./bonusPenalty.module.css";

export type Kind = "bonus" | "penalty";
const MAX_PHOTO_BYTES = 4 * 1024 * 1024;

/** Текст ошибки API для тоста/инлайна (общий с лентой). */
export function errText(e: unknown): string {
  if (e instanceof ApiException) return e.body?.error || `Ошибка ${e.status}`;
  return "Не удалось — проверь связь";
}

interface Props { open: boolean; kind: Kind; operators: BoardOperator[]; onClose: () => void; onDone: (msg: string) => void }

export function BonusPenaltySheet({ open, kind: initKind, operators, onClose, onDone }: Props) {
  const [kind, setKind] = useState<Kind>(initKind);
  const [opId, setOpId] = useState("");
  const [amount, setAmount] = useState(0);
  const [reason, setReason] = useState("");
  const [date, setDate] = useState<Date>(() => todayBusiness());
  const [photo, setPhoto] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [dd, setDd] = useState<HTMLElement | null>(null);
  const [np, setNp] = useState<HTMLElement | null>(null);
  const [pad, setPad] = useState(false);
  const [silent, setSilent] = useState(false);
  const [mark, setMark] = useState(false);
  const [markLabel, setMarkLabel] = useState("");
  const [markPad, setMarkPad] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);

  const genRef = useRef(0);
  const aliveRef = useRef(true);
  useEffect(() => { aliveRef.current = true; return () => { aliveRef.current = false; }; }, []);
  useEffect(() => {
    if (!open) return;
    genRef.current += 1;
    setKind(initKind); setOpId(""); setAmount(0); setReason(""); setDate(todayBusiness()); setPhoto(null);
    setErr(null); setBusy(false); setDd(null); setNp(null); setPad(false);
    setSilent(false); setMark(false); setMarkLabel(""); setMarkPad(false);
  }, [open, initKind]);
  const dismiss = () => { if (!busy) onClose(); };

  const pickPhoto = (e: ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    setErr(null);
    if (!f.type.startsWith("image/")) { setErr("Только изображения (фото, скриншоты)"); return; }
    if (f.size > MAX_PHOTO_BYTES) { setErr("Фото больше 4 МБ — не добавлено"); return; }
    const gen = genRef.current;
    const r = new FileReader();
    r.onload = () => { if (aliveRef.current && genRef.current === gen && typeof r.result === "string") setPhoto(r.result); };
    r.onerror = () => { if (aliveRef.current && genRef.current === gen) setErr("Не удалось прочитать файл"); };
    r.readAsDataURL(f);
  };

  const isPen = kind === "penalty";
  const opName = operators.find((o) => o.id === opId)?.name ?? "";
  const canSend = !!opId && amount > 0 && reason.trim().length > 0 && !busy;
  const send = async () => {
    if (!canSend) return;
    setBusy(true); setErr(null);
    const gen = genRef.current;
    const live = () => aliveRef.current && genRef.current === gen;
    const body = {
      operator_id: opId, amount, reason: reason.trim().slice(0, 500), period_date: fmtDateApi(date),
      ...(silent ? { silent: true } : {}), ...(mark ? { slip_mark: markLabel.trim() || true } : {}),
    };
    try {
      if (!isPen) {
        await api<{ ok: boolean; id: number; sent: boolean }>("/api/bonuses", { method: "POST", body: { ...body, send: true }, retries: 0 });
        if (live()) onDone(silent ? "Премия начислена без уведомления" : "Премия отправлена оператору");
        return;
      }
      const r = await api<{ ok: boolean; id: number }>("/api/penalties", { method: "POST", body: photo ? { ...body, photo_base64: photo } : body, retries: 0 });
      try {
        await api(`/api/penalties/${r.id}/send`, { method: "POST", retries: 0 });
      } catch (e) {
        /* черновик уже создан — отправить можно из ленты */
        if (live()) onDone(`Штраф сохранён черновиком, не отправлен: ${errText(e)}`);
        return;
      }
      if (live()) onDone(silent ? "Штраф начислен без уведомления" : "Штраф отправлен оператору");
    } catch (e) { if (live()) setErr(errText(e)); } finally { if (live()) setBusy(false); }
  };

  const chipSet = isPen ? css.chipPen : css.chipBonus;
  return (
    <>
      <SheetDialog open={open} onDismiss={dismiss} ariaLabel={isPen ? "Выдать штраф" : "Выдать премию"} maxHeight="82dvh">
        <div className={css.sheetHead}>
          <span className={`${css.sheetTitle} ${isPen ? css.tPen : css.tBonus}`}>{isPen ? "⚠️ Штраф" : "🎁 Премия"}</span>
        </div>
        <div className={css.sheetBody}>
          <div className={css.seg}>
            <button type="button" className={`${css.segBtn} ${!isPen ? css.segBonusOn : ""}`} disabled={busy} onClick={() => setKind("bonus")}>🎁 Премия</button>
            <button type="button" className={`${css.segBtn} ${isPen ? css.segPenOn : ""}`} disabled={busy} onClick={() => setKind("penalty")}>⚠️ Штраф</button>
          </div>
          <div className={css.field}>
            <span className={css.fLbl}>Сотрудник</span>
            <button type="button" className={`${css.chip} ${opId ? chipSet : css.chipEmpty}`} onClick={(e) => setDd(e.currentTarget)}>{opName || "выбрать…"}</button>
          </div>
          <div className={css.field}>
            <span className={css.fLbl}>Сумма, ₽</span>
            <button type="button" className={`${css.chip} ${amount > 0 ? chipSet : css.chipEmpty}`} onClick={(e) => setNp(e.currentTarget)}>
              {amount > 0 ? <span className={`${css.money} ${isPen ? css.moneyPen : css.moneyBonus}`}>{isPen ? "−" : ""}₽{amount.toLocaleString("ru")}</span> : "сколько…"}
            </button>
          </div>
          <div className={css.field}>
            <span className={css.fLbl}>За что</span>
            <button type="button" className={`${css.chip} ${reason ? chipSet : css.chipEmpty}`} onClick={() => setPad(true)}>{reason || "обязательно…"}</button>
          </div>
          <div className={css.field}>
            <span className={css.fLbl}>Дата (период зарплаты)</span>
            <div className={css.dateRow}>
              <button type="button" className={css.dateBtn} aria-label="Днём раньше" onClick={() => setDate((d) => addDays(d, -1))}>‹</button>
              <span className={css.dateVal}>{fmtDate(date)}</span>
              <button type="button" className={css.dateBtn} aria-label="Днём позже" onClick={() => setDate((d) => addDays(d, 1))}>›</button>
            </div>
          </div>
          {isPen ? (
            <div className={css.field}>
              <span className={css.fLbl}>Фото · необязательно</span>
              <div className={css.photoRow}>
                {photo ? (
                  <div className={css.ph}>
                    <img src={photo} alt="" />
                    <button type="button" className={css.phX} aria-label="Убрать фото" onClick={() => setPhoto(null)}>✕</button>
                  </div>
                ) : <button type="button" className={css.phAdd} onClick={() => fileRef.current?.click()}>+ фото</button>}
              </div>
              <input ref={fileRef} className={css.hidden} type="file" accept="image/*" onChange={pickPhoto} />
            </div>
          ) : null}
          <div className={css.field}>
            <span className={css.fLbl}>Уведомление и расчётка</span>
            <Switch checked={silent} onChange={setSilent} disabled={busy} accent={isPen ? "var(--red)" : "var(--blue)"} label="Без уведомления" ariaLabel="Без уведомления в Telegram" />
            <span className={css.note}>{silent
              ? "В Telegram ничего не уйдёт — ни сейчас, ни при правке или отмене. В зарплату и расчётку войдёт как обычно."
              : "Сотрудник получит сообщение в Telegram."}</span>
            <Switch checked={mark} onChange={setMark} disabled={busy} accent={isPen ? "var(--red)" : "var(--blue)"} label="★ Отметить в расчётке" ariaLabel="Особая отметка в расчётке" />
            {mark ? (
              <>
                <button type="button" className={`${css.chip} ${markLabel ? chipSet : css.chipEmpty}`} disabled={busy} onClick={() => setMarkPad(true)}>{markLabel || "подпись отметки · необязательно…"}</button>
                <span className={css.note}>В расчётке строка будет выделена: <SlipMarkTag mark={markLabel.trim() || "★"} tone={isPen ? "pen" : "bonus"} /></span>
              </>
            ) : null}
          </div>
          {err ? <div className={css.err}>{err}</div> : null}
        </div>
        <div className={css.foot}>
          <button type="button" className={`${css.btn} ${css.btnCancel}`} disabled={busy} onClick={dismiss}>Отмена</button>
          <button type="button" className={`${css.btn} ${css.btnGo} ${isPen ? css.btnGoPen : ""}`} disabled={!canSend} onClick={() => void send()}>{busy ? "Отправляю…" : silent ? "Выдать без уведомления" : "Выдать и отправить"}</button>
        </div>
      </SheetDialog>
      <Dropdown open={!!dd} anchor={dd} multi={false} title="Сотрудник" selectedIds={opId ? [opId] : []}
        items={[...operators].sort((a, b) => a.name.localeCompare(b.name, "ru")).map((o) => ({ id: o.id, label: o.name }))}
        onCommit={(ids) => { const id = String(ids[0] ?? ""); if (id) setOpId(id); }}
        onClose={() => setDd(null)} />
      <NumPadPop open={!!np} anchor={np} label={isPen ? "Сумма штрафа" : "Сумма премии"} mode="money" value={amount || ""} max={999999}
        onCommit={(v) => setAmount(Math.max(0, Math.round(v)))} onClose={() => setNp(null)} />
      <TextPad open={pad} label="За что" value={reason} maxLength={500} multiline placeholder={isPen ? "Причина штрафа…" : "За что премия…"}
        onCommit={(v) => setReason(v.trim())} onClose={() => setPad(false)} />
      <TextPad open={markPad} label="Подпись отметки" value={markLabel} maxLength={SLIP_MARK_MAX} placeholder="например: KPI сентябрь"
        onCommit={(v) => setMarkLabel(v.replace(/\s+/g, " ").trim())} onClose={() => setMarkPad(false)} />
    </>
  );
}
